import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, ArrowRight, X, AlertTriangle, CreditCard } from "lucide-react";
import Sidebar from "../Sidebar";
import { useTwilioDevice } from "../../hooks/useTwilioDevice";
import { TwilioDeviceContext } from "../../contexts/TwilioDeviceContext";
import IncomingCallOverlay from "../messages/IncomingCallOverlay";
import SoftPhone from "../messages/SoftPhone";
import { useUserDataContext } from "../../context/UserDataContext";
import ThemeProvider from "../ThemeProvider";
import UpgradeGateModal from "../ui/UpgradeGateModal";

interface AppLayoutProps {
  children: React.ReactNode;
  handleSignOut: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children, handleSignOut }) => {
  const { firmData, canManageBilling } = useUserDataContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("This feature");
  const [upgradePlan, setUpgradePlan] = useState<"pro" | "elite">("pro");
  const device = useTwilioDevice();

  const handleUpgradeRequired = useCallback((e: Event) => {
    const detail = (e as CustomEvent).detail;
    setUpgradeFeature(detail?.featureName || detail?.message || "This feature");
    const plan = detail?.requiredPlan?.toLowerCase();
    setUpgradePlan(plan === "elite" || plan === "firm" ? "elite" : "pro");
    setUpgradeModalOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener("upgrade-required", handleUpgradeRequired);
    return () => window.removeEventListener("upgrade-required", handleUpgradeRequired);
  }, [handleUpgradeRequired]);

  const firmLoaded = Boolean(firmData?._id);
  const isOnTrial = firmData?.isOnTrial;
  const trialExpiration = firmData?.stripe?.trialExpiration;
  const daysLeft = trialExpiration
    ? Math.max(0, Math.ceil((new Date(trialExpiration).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;
  const showBanner = firmLoaded && isOnTrial && !bannerDismissed;

  // Subscription suspended — show persistent modal (except on settings pages)
  const isSuspended = firmLoaded && !!(firmData as any)?.stripe?.suspendedAt;
  const isOnSettingsPage = location.pathname.startsWith("/settings");
  const showSuspendedModal = isSuspended && !isOnSettingsPage;

  return (
    <TwilioDeviceContext.Provider value={device}>
      <ThemeProvider>
        <div className="min-h-screen w-full flex" style={{ backgroundColor: "#F4F6F8" }}>
          <Sidebar handleSignOut={handleSignOut} />
          <div className="flex-1 overflow-auto">
            {showBanner && (
              <div className="px-6 pt-4">
                <div className="rounded-xl px-5 py-3 flex items-center justify-between shadow-sm" style={{ background: "linear-gradient(to right, var(--color-accent), var(--color-primary))" }}>
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        You're on a free trial  -  {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining
                      </p>
                      <p className="text-xs text-white/70">
                        Upgrade now to keep all your data and features
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManageBilling ? (
                      <button
                        onClick={() => navigate("/settings/subscription")}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-brand rounded-lg text-sm font-bold hover:bg-brand-light transition-colors"
                      >
                        Upgrade Now
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <span className="text-xs text-white/80 font-medium">
                        Contact your administrator to upgrade
                      </span>
                    )}
                    <button
                      onClick={() => setBannerDismissed(true)}
                      className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="max-w-[1600px] mx-auto px-6 py-8">
              {children}
            </div>
          </div>
        </div>

        {/* Global incoming call overlay (shows on all pages) */}
        <IncomingCallOverlay
          incomingCall={device.incomingCall}
          callState={device.callState}
          onAccept={device.acceptCall}
          onReject={device.rejectCall}
        />

        {/* Global soft phone (shows during active calls) */}
        <SoftPhone
          callState={device.callState}
          onHangup={device.hangup}
          onToggleMute={device.toggleMute}
          onAccept={device.acceptCall}
          onReject={device.rejectCall}
        />

        {/* Global upgrade required modal */}
        <UpgradeGateModal
          open={upgradeModalOpen}
          onClose={() => setUpgradeModalOpen(false)}
          featureName={upgradeFeature}
          requiredPlan={upgradePlan}
        />

        {/* Subscription suspended modal */}
        {showSuspendedModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-8 max-w-md mx-4 text-center">
              <div className="h-16 w-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="h-8 w-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">
                Subscription Suspended
              </h2>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                We were unable to process your payment. Please update your billing
                information to restore access to all features. Your data is safe
                and will not be deleted.
              </p>
              {canManageBilling ? (
                <button
                  onClick={() => navigate("/settings/subscription")}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold transition-colors w-full justify-center"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
                >
                  <CreditCard className="h-4 w-4" />
                  Update Billing
                </button>
              ) : (
                <div className="px-4 py-3 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-600">
                    Please contact your firm administrator to update the billing information.
                  </p>
                </div>
              )}
              <p className="text-xs text-slate-400 mt-4">
                Need help? Contact us at billing@kavoa.io
              </p>
            </div>
          </div>
        )}
      </ThemeProvider>
    </TwilioDeviceContext.Provider>
  );
};

export default AppLayout;
